import { useState, useEffect } from 'react';

export default function useProducts(businessName, salePointName) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    fetch(`/api/products/${businessName}/${salePointName}/getAllProducts`)
      .then(res => {
        if (!res.ok) {
            setNotFound(true);
          throw new Error(`API error: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        if (!Array.isArray(data)) throw new Error("Invalid response format");
        setProducts(data);
      })
      .catch(err => {
        console.error("Fetch error in useProducts:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [businessName, salePointName]);

  // Once products are loaded, bulk-fetch live stock for every non-variant
  // product (variant products need a combination picked first, so their
  // quantity isn't meaningful at the list level and is left undefined —
  // ProductCard simply won't render a quantity line for those).
  useEffect(() => {
    if (products.length === 0) return;

    const plainProducts = products.filter(p => !p.hasVariants && !p.sellIfNotProvidedInStore);
    if (plainProducts.length === 0) return;

    const items = plainProducts.map(p => ({ itemId: p.productOriginalId, combId: 0 }));

    fetch(`/api/products/${businessName}/${salePointName}/stock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    })
      .then(res => res.json())
      .then(results => {
        if (!Array.isArray(results)) return;

        setProducts(prev =>
          prev.map(p => {
            if (p.hasVariants) return p;
            const match = results.find(r => r.itemId === p.productOriginalId);
            return match && typeof match.quantity === 'number'
              ? { ...p, stockQuantity: match.quantity }
              : p;
          })
        );
      })
      .catch(err => console.error('Failed to fetch stock for product list', err));
    // Only re-run when the underlying product list identity changes (new
    // fetch), not on every stockQuantity merge — otherwise this would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length, businessName, salePointName]);

  return { products, loading, notFound };
}